import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { DangerButton, PrimaryButton } from '../../components/buttons';
import { Loading } from '../../components/loading';
import { Page } from '../../components/page';
import { useBulkDeleteOrders, useCreateOrder, useDraftOrders } from '../../hooks/orders';
import { useVendor } from '../../hooks/vendors';
import { orderCleanupRoute, storeLayout } from '../../router';

export function OrderCleanup() {
  const { vendorId } = orderCleanupRoute.useSearch();
  const { vendor } = useVendor(vendorId);
  const { store } = storeLayout.useRouteContext();
  const bulkDeleteOrders = useBulkDeleteOrders();
  const { draftOrdersByVendor } = useDraftOrders();
  const draftOrders = draftOrdersByVendor(vendorId);
  const [loading, setLoading] = useState(false);

  const createOrder = useCreateOrder();
  const navigate = useNavigate();

  const navigateToOrder = useCallback(
    (orderId: string) => {
      navigate({
        to: '/stores/$storeId/orders/$orderId/edit',
        params: { storeId: store.id, orderId },
      });
    },
    [navigate, store]
  );

  const deleteExistingOrdersAndCreateNew = useCallback(() => {
    setLoading(true);
    bulkDeleteOrders(draftOrders)
      .then(() => {
        createOrder(store.id, { vendor_id: vendorId }).then((order) => {
          navigateToOrder(order.id);
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [store, vendorId, draftOrders, createOrder, navigateToOrder, bulkDeleteOrders]);

  const openMostRecentOrder = useCallback(() => {
    const mostRecentOrder = draftOrders[0];
    navigateToOrder(mostRecentOrder.id);
  }, [draftOrders, navigateToOrder]);

  if (draftOrders.length === 0 || loading) {
    return <Loading />;
  }

  const existingOrderLabelWithCount =
    draftOrders.length === 1 ? 'existing order' : `${draftOrders.length} existing orders`;
  const createDescription = `Discard the ${existingOrderLabelWithCount} and create a new one. Any updates to the existing order(s) will be lost.`;
  const openDescription = `Continue with the most recent order (updated ${new Date(draftOrders[0].updated_at).toLocaleDateString()}).`;

  return (
    <Page title="Existing Order Found">
      <div className="flex flex-col gap-4 max-w-2xl mx-auto">
        <div className="flex items-center gap-2 text-orange-500 pt-10">
          <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
          <span>
            {draftOrders.length === 1
              ? `There is an existing draft order for ${vendor?.name || 'this vendor'}. `
              : `There are ${draftOrders.length} existing draft orders for ${vendor?.name || 'this vendor'}. `}
            Would you like to:
          </span>
        </div>
        <ActionPanel title="Open Existing" description={openDescription}>
          <PrimaryButton onClick={openMostRecentOrder}>Open Existing</PrimaryButton>
        </ActionPanel>
        <ActionPanel title="Cancel &amp; Create New" description={createDescription}>
          <DangerButton onClick={deleteExistingOrdersAndCreateNew}>
            Cancel &amp; Create New
          </DangerButton>
        </ActionPanel>
      </div>
    </Page>
  );
}

function ActionPanel({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-gray-50 rounded-md">
      <div className="p-4 sm:p-5">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <div className="mt-2 text-sm text-gray-500">
              <p>{description}</p>
            </div>
          </div>
          <div className="flex-shrink-0">{children}</div>
        </div>
      </div>
    </div>
  );
}
